import { Plus, Trash2 } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import type { CustomMCPServer } from '../../types/wizard';
import clsx from 'clsx';

function MultiCheckbox({
  label,
  description,
  options,
  selected,
  onChange,
}: {
  label: string;
  description?: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
      </label>
      {description && (
        <p className="text-xs text-gray-500 mb-3">{description}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            onClick={() => toggle(option)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150',
              selected.includes(option)
                ? 'bg-accent/10 border-accent/30 text-accent-300'
                : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300 hover:text-gray-300'
            )}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MCPConfigStep() {
  const { mcpConfig, updateMCPConfig } = useWizardStore();
  const { options, loading } = useWizardOptions();

  const ticketSystems = options?.mcpConfig.ticketSystems ?? [];
  const documentationOptions = options?.mcpConfig.documentationOptions ?? [];
  const communicationOptions = options?.mcpConfig.communicationOptions ?? [];
  const sourceControlOptions = options?.mcpConfig.sourceControlOptions ?? [];
  const cloudConsoleOptions = options?.mcpConfig.cloudConsoleOptions ?? [];
  const monitoringOptions = options?.mcpConfig.monitoringOptions ?? [];

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const addCustomServer = () => {
    updateMCPConfig({
      customServers: [
        ...mcpConfig.customServers,
        { name: '', command: '', args: '', description: '' },
      ],
    });
  };

  const updateCustomServer = (
    index: number,
    field: keyof CustomMCPServer,
    value: string
  ) => {
    const updated = [...mcpConfig.customServers];
    updated[index] = { ...updated[index], [field]: value };
    updateMCPConfig({ customServers: updated });
  };

  const removeCustomServer = (index: number) => {
    updateMCPConfig({
      customServers: mcpConfig.customServers.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          MCP Configuration
        </h2>
        <p className="text-sm text-gray-500">
          Configure Model Context Protocol servers to connect Claude to your
          development tools.
        </p>
      </div>

      {/* Ticket System */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Ticket System
        </label>
        <select
          value={mcpConfig.ticketSystem}
          onChange={(e) => updateMCPConfig({ ticketSystem: e.target.value })}
          className="select-field"
        >
          <option value="">Select ticket system...</option>
          {ticketSystems.map((ts) => (
            <option key={ts} value={ts}>
              {ts}
            </option>
          ))}
        </select>
      </div>

      {/* Documentation */}
      <MultiCheckbox
        label="Documentation"
        description="Where does your team keep documentation?"
        options={documentationOptions}
        selected={mcpConfig.documentation}
        onChange={(documentation) => updateMCPConfig({ documentation })}
      />

      {/* Communication */}
      <MultiCheckbox
        label="Communication"
        options={communicationOptions}
        selected={mcpConfig.communication}
        onChange={(communication) => updateMCPConfig({ communication })}
      />

      {/* Source Control */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Source Control
        </label>
        <select
          value={mcpConfig.sourceControl}
          onChange={(e) =>
            updateMCPConfig({ sourceControl: e.target.value })
          }
          className="select-field"
        >
          <option value="">Select source control platform...</option>
          {sourceControlOptions.map((sc) => (
            <option key={sc} value={sc}>
              {sc}
            </option>
          ))}
        </select>
      </div>

      {/* Cloud Console */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Cloud Console
        </label>
        <select
          value={mcpConfig.cloudConsole}
          onChange={(e) =>
            updateMCPConfig({ cloudConsole: e.target.value })
          }
          className="select-field"
        >
          <option value="">Select cloud console...</option>
          {cloudConsoleOptions.map((cc) => (
            <option key={cc} value={cc}>
              {cc}
            </option>
          ))}
        </select>
      </div>

      {/* Monitoring */}
      <MultiCheckbox
        label="Monitoring"
        description="Monitoring and observability tools"
        options={monitoringOptions}
        selected={mcpConfig.monitoring}
        onChange={(monitoring) => updateMCPConfig({ monitoring })}
      />

      {/* Custom MCP Servers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-300">
              Custom MCP Servers
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              Add custom MCP server configurations
            </p>
          </div>
          <button
            onClick={addCustomServer}
            className="btn-secondary text-sm py-2 px-3"
          >
            <Plus className="h-4 w-4" />
            Add Server
          </button>
        </div>

        <div className="space-y-4">
          {mcpConfig.customServers.map((server, index) => (
            <div
              key={index}
              className="p-4 rounded-lg bg-surface-DEFAULT border border-dark-border space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-400">
                  Server #{index + 1}
                </span>
                <button
                  onClick={() => removeCustomServer(index)}
                  className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={server.name}
                    onChange={(e) =>
                      updateCustomServer(index, 'name', e.target.value)
                    }
                    placeholder="e.g., my-mcp-server"
                    className="input-field py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Command
                  </label>
                  <input
                    type="text"
                    value={server.command}
                    onChange={(e) =>
                      updateCustomServer(index, 'command', e.target.value)
                    }
                    placeholder="e.g., npx"
                    className="input-field py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Args
                  </label>
                  <input
                    type="text"
                    value={server.args}
                    onChange={(e) =>
                      updateCustomServer(index, 'args', e.target.value)
                    }
                    placeholder="e.g., -y @my-org/mcp-server"
                    className="input-field py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={server.description}
                    onChange={(e) =>
                      updateCustomServer(index, 'description', e.target.value)
                    }
                    placeholder="What does this server do?"
                    className="input-field py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}

          {mcpConfig.customServers.length === 0 && (
            <div className="text-center py-8 text-gray-600 text-sm">
              No custom servers configured. Click &quot;Add Server&quot; to add one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
